import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Statistic, Typography, Button, Table, Tag, Space, Spin } from 'antd';
import {
  TeamOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  BookOutlined,
  ArrowRightOutlined,
  PlusOutlined,
  FolderOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTenantStore } from '../../stores/tenantStore';
import { proposalApi } from '../../services/proposal';
import { knowledgeApi } from '../../services/knowledge';
import { ROUTES } from '../../config/routes';
import type { Proposal } from '../../types/proposal';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { PROPOSAL_STATUS_CONFIG } from '../../constants/proposalStatus';
import { STAT_COLORS, COLORS } from '../../constants/colors';
import { SPACING, RADIUS, FONT_SIZE, FONT_WEIGHT, SIZE } from '../../constants/design';

const { Title, Text } = Typography;

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { tenant, fetchTenant } = useTenantStore();
  const [recentProposals, setRecentProposals] = useState<Proposal[]>([]);
  const [proposalsLoading, setProposalsLoading] = useState(false);
  const [dashboardStats, setDashboardStats] = useState({
    totalCustomers: 0,
    totalProposals: 0,
    activeProposals: 0,
    knowledgeItems: 0,
  });

  useEffect(() => {
    fetchTenant();
    fetchRecentProposals();
    fetchDashboardStats();
  }, [fetchTenant]);

  // 获取最近提案
  const fetchRecentProposals = async () => {
    setProposalsLoading(true);
    try {
      const response = await proposalApi.getProposals({ page: 1, page_size: 5 });
      // 后端返回格式: { data: T[], pagination: {...} }
      setRecentProposals(response.data || []);
    } catch (error) {
      // 如果API失败，使用空数据
      setRecentProposals([]);
    } finally {
      setProposalsLoading(false);
    }
  };

  // 获取统计数据
  const fetchDashboardStats = async () => {
    const [customersRes, proposalsRes, analyzingRes, requirementReadyRes, knowledgeRes] = await Promise.allSettled([
      proposalApi.getCustomers({ page: 1, page_size: 1 }),
      proposalApi.getProposals({ page: 1, page_size: 1 }),
      proposalApi.getProposals({ page: 1, page_size: 1, status: 'analyzing' }),
      proposalApi.getProposals({ page: 1, page_size: 1, status: 'requirement_ready' }),
      knowledgeApi.getEntries({ page: 1, page_size: 1 }),
    ]);

    // 活跃提案 = analyzing + requirement_ready 两个状态的总数之和
    const analyzingTotal = analyzingRes.status === 'fulfilled' ? (analyzingRes.value.pagination?.total || 0) : 0;
    const requirementReadyTotal = requirementReadyRes.status === 'fulfilled' ? (requirementReadyRes.value.pagination?.total || 0) : 0;

    // 后端返回格式: { data: T[], pagination: { total, page, page_size, total_pages } }
    setDashboardStats({
      totalCustomers: customersRes.status === 'fulfilled' ? (customersRes.value.pagination?.total || 0) : 0,
      totalProposals: proposalsRes.status === 'fulfilled' ? (proposalsRes.value.pagination?.total || 0) : 0,
      activeProposals: analyzingTotal + requirementReadyTotal,
      knowledgeItems: knowledgeRes.status === 'fulfilled' ? (knowledgeRes.value.pagination?.total || 0) : 0,
    });

    // 记录失败的请求
    if (customersRes.status === 'rejected') console.error('[Dashboard] 获取客户统计失败:', customersRes.reason);
    if (proposalsRes.status === 'rejected') console.error('[Dashboard] 获取提案统计失败:', proposalsRes.reason);
    if (analyzingRes.status === 'rejected') console.error('[Dashboard] 获取 analyzing 提案统计失败:', analyzingRes.reason);
    if (requirementReadyRes.status === 'rejected') console.error('[Dashboard] 获取 requirement_ready 提案统计失败:', requirementReadyRes.reason);
    if (knowledgeRes.status === 'rejected') console.error('[Dashboard] 获取知识库统计失败:', knowledgeRes.reason);
  };

  // 统计卡片数据
  const stats = [
    {
      title: '客户总数',
      value: dashboardStats.totalCustomers,
      icon: <TeamOutlined />,
      color: STAT_COLORS.customers,
      link: ROUTES.CUSTOMERS,
    },
    {
      title: '提案总数',
      value: dashboardStats.totalProposals,
      icon: <FileTextOutlined />,
      color: STAT_COLORS.proposals,
      link: ROUTES.PROPOSALS,
    },
    {
      title: '进行中提案',
      value: dashboardStats.activeProposals,
      icon: <ClockCircleOutlined />,
      color: STAT_COLORS.activeProposals,
      link: ROUTES.PROPOSALS,
    },
    {
      title: '知识库条目',
      value: dashboardStats.knowledgeItems,
      icon: <BookOutlined />,
      color: STAT_COLORS.knowledge,
      link: ROUTES.KNOWLEDGE,
    },
  ];

  // 快捷操作
  const quickActions = [
    {
      title: '新建客户',
      desc: '添加新的客户信息',
      icon: <TeamOutlined />,
      link: ROUTES.CUSTOMERS,
    },
    {
      title: '新建提案',
      desc: '创建红人营销提案',
      icon: <FileTextOutlined />,
      link: ROUTES.PROPOSAL_NEW,
    },
    {
      title: '进入知识库',
      desc: '管理业务知识数据',
      icon: <BookOutlined />,
      link: ROUTES.KNOWLEDGE,
    },
  ];

  // 提案列表列定义
  const proposalColumns: ColumnsType<Proposal> = [
    {
      title: '提案标题',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (text: string, record: Proposal) => (
        <a onClick={() => navigate(`/proposals/${record.id}`)}>{text}</a>
      ),
    },
    {
      title: '客户',
      dataIndex: ['customer', 'company_name'],
      key: 'customer',
      width: 150,
      ellipsis: true,
      render: (text: string) => text || '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        const statusInfo = PROPOSAL_STATUS_CONFIG[status] || { text: status, color: 'default' };
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date: string) => (date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-'),
    },
  ];

  return (
    <div>
      {/* 欢迎区域 */}
      <div style={{ marginBottom: SPACING.xl }}>
        <Title level={4} style={{ marginBottom: SPACING.xs }}>
          欢迎回来，{tenant?.company_name || '您的公司'}
        </Title>
        <Text type="secondary">
          当前订阅：{tenant?.subscription_plan === 'trial' ? '试用版' : tenant?.subscription_plan || '未订阅'}
        </Text>
      </div>

      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: SPACING.xl }}>
        {stats.map((stat) => (
          <Col xs={24} sm={12} lg={6} key={stat.title}>
            <Card
              hoverable
              onClick={() => navigate(stat.link)}
              style={{ borderRadius: RADIUS.lg }}
            >
              <Statistic
                title={stat.title}
                value={stat.value}
                valueStyle={{ color: stat.color, fontWeight: FONT_WEIGHT.semibold }}
                prefix={stat.icon}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* 最近提案列表 */}
      <Card
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>最近提案</span>
            <Button type="link" onClick={() => navigate(ROUTES.PROPOSALS)}>
              查看全部 <ArrowRightOutlined />
            </Button>
          </div>
        }
        style={{ marginBottom: SPACING.xl, borderRadius: RADIUS.lg }}
      >
        <Spin spinning={proposalsLoading}>
          <Table
            columns={proposalColumns}
            dataSource={recentProposals}
            rowKey="id"
            pagination={false}
            size="middle"
            locale={{ emptyText: '暂无提案数据' }}
          />
        </Spin>
      </Card>

      {/* 快捷操作 */}
      <Title level={5} style={{ marginBottom: SPACING.base }}>
        快捷操作
      </Title>
      <Row gutter={[16, 16]}>
        {quickActions.map((action) => (
          <Col xs={24} sm={12} lg={8} key={action.title}>
            <Card
              hoverable
              onClick={() => navigate(action.link)}
              style={{ borderRadius: RADIUS.lg, cursor: 'pointer' }}
              styles={{ body: { padding: SPACING.lg } }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.base }}>
                <div
                  style={{
                    width: SIZE.iconXl,
                    height: SIZE.iconXl,
                    borderRadius: RADIUS.lg,
                    background: 'var(--color-bg-light)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: FONT_SIZE.xl,
                    color: 'var(--color-primary)',
                  }}
                >
                  {action.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <Text strong style={{ fontSize: FONT_SIZE.md, display: 'block', marginBottom: SPACING.xs }}>
                    {action.title}
                  </Text>
                  <Text type="secondary" style={{ fontSize: FONT_SIZE.sm }}>
                    {action.desc}
                  </Text>
                </div>
                <ArrowRightOutlined style={{ color: 'var(--color-text-muted)' }} />
              </div>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};
